import type React from "react"
import { Switch, FormControlLabel, Box, Typography } from "@mui/material"

interface ToggleProps {
  value: boolean
  onChange: (newValue: boolean) => void
  label?: string
  disabled?: boolean
  size?: "small" | "medium"
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning"
  labelPlacement?: "end" | "start" | "top" | "bottom"
}

export default function Toggle({
  value,
  onChange,
  label,
  disabled = false,
  size = "medium",
  color = "primary",
  labelPlacement = "end",
}: ToggleProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked)
  }

  // Si no hay label, solo retornar el Switch
  if (!label) {
    return <Switch checked={value} onChange={handleChange} disabled={disabled} size={size} color={color} />
  }

  // Si hay label, usar FormControlLabel
  return (
    <FormControlLabel
      control={<Switch checked={value} onChange={handleChange} disabled={disabled} size={size} color={color} />}
      label={label}
      labelPlacement={labelPlacement}
    />
  )
}

// Componente adicional para casos más avanzados
interface ToggleWithDescriptionProps extends ToggleProps {
  description?: string
  helperText?: string
}

export function ToggleWithDescription({
  value,
  onChange,
  label,
  description,
  helperText,
  disabled = false,
  size = "medium",
  color = "primary",
}: ToggleWithDescriptionProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked)
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          {label && (
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          )}
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        <Switch checked={value} onChange={handleChange} disabled={disabled} size={size} color={color} />
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}
